import { Icon, iconSizes, Money } from "@hubrise/react-components"
import * as ReactTable from "@tanstack/react-table"
import { range } from "lodash"
import * as React from "react"

import CheckboxCell from "Catalog/Grid/shared/components/GridTable/Cell/CheckboxCell"
import MoneyCell from "Catalog/Grid/shared/components/GridTable/Cell/MoneyCell"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"

import InputCell from "../Cell/InputCell"
import StaticCell from "../Cell/StaticCell"
import { RowData } from "../types"
import { accessorKey } from "../utils"

import { Icons, IconWrapper, StyledRow } from "./styles"

type AllUndefined<T> = {
  [P in keyof T]: undefined
}

interface BasePublicRowProps<ItemType> {
  selectItemPath: (item: ItemType) => string
  editItemPath: (item: ItemType) => string
  previewItemPath: ((item: ItemType) => string) | undefined
  designators: (item: ItemType) => Array<string>
  itemRef: (item: ItemType) => string // ref is a reserved word
  setRef: (item: ItemType, ref: string) => void
  isEnabled: (item: ItemType, variantRef?: string) => boolean
  enable: (item: ItemType, enabled: boolean, variantRef?: string) => void
}

interface PricePublicRowProps<ItemType> {
  catalogPrice: (item: ItemType) => Money
  setCatalogPrice: (item: ItemType, price: Money) => void
  variantPrice: (item: ItemType, variantRef: string) => Money | null
  setVariantPrice: (item: ItemType, variantRef: string, price: Money | null) => void
}

// These properties are exposed by the GridTable component.
export type PublicRowProps<ItemType> =
  | (BasePublicRowProps<ItemType> & AllUndefined<PricePublicRowProps<ItemType>>) // Deals
  | (BasePublicRowProps<ItemType> & PricePublicRowProps<ItemType>) // SKUs and options

interface PrivateRowProps<ItemType extends { id: number }> {
  index: number
  item: ItemType
  row: ReactTable.Row<RowData<ItemType>>
  columns: Array<ReactTable.ColumnDef<RowData<ItemType>>>
  isSelected: boolean
  navigateNoUpdate: ReturnType<typeof useNavigateNoUpdate>
  style?: React.CSSProperties
}

const Row = <ItemType extends { id: number }>({
  index,
  item,
  row,
  columns,
  isSelected,
  navigateNoUpdate,
  selectItemPath,
  editItemPath,
  previewItemPath,
  designators,
  itemRef,
  setRef,
  catalogPrice,
  setCatalogPrice,
  variantPrice,
  setVariantPrice,
  isEnabled,
  enable,
  style,
}: PrivateRowProps<ItemType> & PublicRowProps<ItemType>): JSX.Element => {
  const { catalog } = useEditor()
  const { currency } = catalog

  const [enabled, setEnabled] = React.useState(isEnabled(item))
  const [isMouseOver, setIsMouseOver] = React.useState(false)

  const itemDesignators = designators(item)
  const columnWidths = row.getVisibleCells().map((cell) => cell.column.getSize())

  return (
    <div style={{ ...style, width: "auto" }}>
      <StyledRow
        onClick={() => navigateNoUpdate(selectItemPath(item))}
        onMouseEnter={() => setIsMouseOver(true)}
        onMouseLeave={() => setIsMouseOver(false)}
        key={
          // Re-render the row when the item is enabled/disabled
          enabled.toString()
        }
        data-testid={`row:${index}`}
      >
        {columns.map((column, index) => {
          const meta = column.meta!
          const { columnType } = meta
          const width = columnWidths[index]

          const commonCellProps = {
            key: index,
            isSelected,
            isMouseOver,
            width: width,
            stickyPosition:
              meta.stickyIndex !== undefined
                ? range(0, meta.stickyIndex).reduce((acc, index) => acc + columnWidths[index], 0)
                : undefined,
            isLastSticky: meta.isLastSticky,
            "data-testid": `cell:${item.id}:${accessorKey(column)}`,
          }

          switch (columnType.type) {
            case "designator": {
              const index = columnType.index
              const designator = itemDesignators[index]
              return (
                <StaticCell
                  value={designator}
                  linethrough={index === itemDesignators.length - 1 && !isEnabled(item)}
                  {...commonCellProps}
                />
              )
            }

            case "ref_code":
              return (
                <InputCell initialValue={itemRef(item)} onChange={(ref) => setRef(item, ref)} {...commonCellProps} />
              )

            case "enabled": {
              const variantRefEnabled = columnType.variantRef
              if (variantRefEnabled === null) {
                return (
                  <CheckboxCell
                    initialValue={isEnabled(item)}
                    onChange={(enabled) => {
                      setEnabled(enabled) // Re-render the row to show/hide variant checkboxes
                      enable(item, enabled)
                    }}
                    {...commonCellProps}
                  />
                )
              } else {
                return (
                  <CheckboxCell
                    initialValue={isEnabled(item, variantRefEnabled)}
                    onChange={(enabled) => enable(item, enabled, variantRefEnabled)}
                    hideCheckbox={!isEnabled(item)}
                    {...commonCellProps}
                  />
                )
              }
            }

            case "price": {
              if (!catalogPrice) throw new Error("catalogPrice is required")
              const variantRefPrice = columnType.variantRef
              if (variantRefPrice === null) {
                return (
                  <MoneyCell
                    initialValue={catalogPrice(item)}
                    isRequired={true}
                    currency={currency}
                    onChange={(price) => setCatalogPrice(item, price ?? new Money(0, currency))}
                    {...commonCellProps}
                  />
                )
              } else {
                return (
                  <MoneyCell
                    initialValue={variantPrice(item, variantRefPrice)}
                    placeholder={catalogPrice(item)}
                    isRequired={false}
                    currency={currency}
                    onChange={(price) => setVariantPrice(item, variantRefPrice, price)}
                    {...commonCellProps}
                  />
                )
              }
            }
          }
        })}

        <Icons>
          <IconWrapper
            onClick={(event) => {
              navigateNoUpdate(editItemPath(item))
              event.stopPropagation()
            }}
          >
            <Icon code="edit" size={iconSizes._20} />
          </IconWrapper>

          {previewItemPath && (
            <IconWrapper
              onClick={(event) => {
                navigateNoUpdate(previewItemPath(item))
                event.stopPropagation()
              }}
            >
              <Icon code="visibility" size={iconSizes._20} />
            </IconWrapper>
          )}
        </Icons>
      </StyledRow>
    </div>
  )
}

const MemoisedRow = React.memo(Row, (prevProps, nextProps) => {
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isEnabled(prevProps.item) === nextProps.isEnabled(nextProps.item) &&
    prevProps.columns === nextProps.columns
  )
})

export default MemoisedRow as typeof Row
